/**
 * The decisions behind a pending plan change.
 *
 * A proposal is announced in two places — the Plans page (the merchant's
 * own switch) and the app-wide `PlanChangeApprovalBanner` (a daily-check
 * proposal). Both ask the same three questions of the same wire object:
 *
 *   whose is it   — `merchantProposal` / `systemProposal`
 *   when does an approval land — `takesEffect`
 *   has it been dismissed — `useProposalDismissal`
 *
 * No i18n and no formatters: dates arrive already formatted, and timing
 * is returned as a KIND the caller maps to its own copy.
 *
 * The argument everywhere is the PROPOSAL object off
 * `billing_status_payload` (`pending_tier_change`), snake_case as the wire
 * sends it.
 */

import { useSessionDismissal } from '../utils/noticeDismissal';

/**
 * A plan change the merchant asked for — the switch they started on
 * the Plans page and have not approved on Shopify yet.
 */
export function merchantProposal(change) {
  return proposalFrom(change, 'merchant');
}

/**
 * A plan change the daily tier re-evaluation pushed for approval. News
 * the merchant has no reason to be looking for, and an unapproved
 * upgrade pauses their access — which is why it is app-wide, and why
 * it reads with a deadline the merchant's own switch never carries.
 */
export function systemProposal(change) {
  return proposalFrom(change, 'daily_check');
}

/**
 * Whether a proposal can actually be rendered, whoever asked for it.
 *
 * No confirmation URL is a broken payload rather than a change: an
 * Approve button with nowhere to send the merchant is worse than no
 * banner at all.
 */
function renderableProposal(change) {
  // The proposal's presence IS the pending-approval answer:
  // billing/payloads.py fills `needs_approval` from the same PENDING
  // proposal row, so it is not read a second time.
  if (!change?.confirmation_url) return null;
  return change;
}

function proposalFrom(change, trigger) {
  if (change?.trigger !== trigger) return null;
  return renderableProposal(change);
}

/**
 * WHEN an approval would take effect, from the behaviour stored on the
 * proposal — never inferred from the price direction: an equal-price
 * interval switch swaps immediately and is an upgrade in neither
 * direction.
 *
 * Returns `{ kind: 'on_approval' }`, `{ kind: 'next_cycle', date }`, or
 * null — null meaning SAY NOTHING about timing, which covers both a
 * proposal with no stored behaviour and a deferred change whose renewal
 * date we don't have.
 *
 * @param {object|null} change      the proposal
 * @param {string}      renewalDate the store's next renewal, ALREADY
 *                                  formatted by the calling surface
 *                                  ('' when the payload carries none)
 */
export function takesEffect(change, renewalDate) {
  if (change?.takes_effect === 'on_approval') return { kind: 'on_approval' };
  if (change?.takes_effect === 'next_cycle' && renewalDate) {
    return { kind: 'next_cycle', date: renewalDate };
  }
  return null;
}

/**
 * The two things a surface can say about one plan change, and the two
 * things a merchant can dismiss independently.
 *
 * Both rows come off `SubscriptionProposal`, so both carry the SAME
 * `id` — a switch that is PENDING today is the same row that reads
 * DECLINED tomorrow. The notice is therefore part of the key, or
 * dismissing "waiting for your approval" would pre-dismiss "it was not
 * approved" about the very same switch.
 */
export const NOTICE = { PENDING: 'pending', OUTCOME: 'outcome' };

/**
 * The refusals `withdraw_switch` can answer with, all meaning one thing to
 * the merchant: the request is no longer theirs to withdraw. They stay
 * distinct on the wire because the backend logs them apart — a race with
 * the reconciler is not a race with the daily check.
 */
export const WITHDRAW_STALE_CODES = [
  'no_pending_change', 'not_your_change', 'change_already_resolved',
];

/**
 * Per-notice dismissal, for the current browser session.
 *
 * Keyed on (notice, row id), never on the proposal's contents: merchant
 * proposals carry no deadline, so a key built from (tier, deadline) would
 * be shared by every switch to the same tier.
 *
 * Safe to call with a null row (nothing to dismiss, never dismissed) so
 * the caller can run it before its own render guards, as a hook must.
 *
 * @param {object|null} row     the proposal, or the unapproved change
 * @param {string}      notice  which one, from `NOTICE`
 * @returns {[boolean, Function]} `[dismissed, dismiss]`
 */
export function useProposalDismissal(row, notice) {
  return useSessionDismissal(
    row ? `manexys.planChange.dismissed:${notice}:${row.id}` : null,
  );
}
